import React from 'react';
import { Stack, Typography } from '@mui/material';
import {
  EmptyState, ListTable, Panel, RevealText, SearchField, SegmentedControl, ToolbarButton, type ListColumn,
} from '../../../ui-kit';
import { fetchCallbacks, type Callback } from '../../../services/roccoService';
import { fromNow, inZone, isViewersClock, matches, onDay, useLive } from './live';
import ListDownload from './ListDownload';
import AgentName from './AgentName';
import LeadStoryModal from './LeadStory';

/**
 * Times a lead agreed, on a call, for an advisor to ring them back.
 *
 * Every row is the hand-off made visible: the AI agent that booked it, and
 * the person who now owns it. Soonest first, because that is the order the
 * advisors work them in.
 */
type Due = 'today' | 'tomorrow' | 'later';

/** Whether a callback falls today, tomorrow or later, by the viewer's own calendar. */
function dueOf(iso: string | null): Due {
  if (!iso) return 'later';
  const day = new Date(iso);
  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(today.getDate() + 1);
  const same = (a: Date, b: Date) => a.toDateString() === b.toDateString();
  return same(day, today) || day < today ? 'today' : same(day, tomorrow) ? 'tomorrow' : 'later';
}

export default function CallbacksPage() {
  const { data, error, loading, reconnecting } = useLive(fetchCallbacks);
  const [open, setOpen] = React.useState<{ lead: string; call?: string | null } | null>(null);
  const [query, setQuery] = React.useState('');
  const [due, setDue] = React.useState<Due | 'all'>('all');
  const all = data?.callbacks ?? [];
  const byDue = (d: Due) => all.filter((row) => dueOf(row.callback_at) === d);
  const rows = (due === 'all' ? all : byDue(due))
    .filter((row) => matches(query, [row.name, row.phone, row.owner, row.booked_by, row.channel, row.state]));

  const columns: ListColumn<Callback>[] = [
    {
      key: 'when',
      sortValue: (row) => row.callback_at,
      header: 'Callback',
      width: 290,
      // The time as the lead agreed it on the call, with its zone named; under
      // it, the viewer's own time when that differs, so "5 pm EDT" booked from
      // India is not mistaken for 2:30 am.
      render: (row) => (
        <Stack>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary, fontWeight: 600, fontVariantNumeric: 'tabular-nums' })}>
            {inZone(row.callback_at, row.callback_tz)}
          </Typography>
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>
            {isViewersClock(row.callback_at, row.callback_tz)
              ? fromNow(row.callback_at)
              : `${onDay(row.callback_at)} your time · ${fromNow(row.callback_at)}`}
          </Typography>
        </Stack>
      ),
    },
    {
      key: 'lead',
      sortValue: (row) => row.name,
      header: 'Lead',
      grow: 1.1,
      minWidth: 140,
      render: (row) => (
        <Stack>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>{row.name}</Typography>
          <RevealText value={row.phone} what="phone number" />
        </Stack>
      ),
    },
    {
      key: 'owner',
      sortValue: (row) => row.owner,
      header: 'Advisor',
      grow: 1.1,
      minWidth: 150,
      render: (row) => row.owner
        ? <AgentName person={{ name: row.owner, kind: 'human' }} subtitle="Calls back" />
        : null,
    },
    {
      key: 'booked_by',
      sortValue: (row) => row.booked_by,
      header: 'Booked by',
      grow: 1,
      minWidth: 140,
      render: (row) => row.booked_by
        ? <AgentName person={{ name: row.booked_by, kind: 'ai' }}
            subtitle={row.booked_at ? `on a call ${fromNow(row.booked_at)}` : undefined} />
        : <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>—</Typography>,
    },
    {
      key: 'call',
      header: '',
      width: 140,
      align: 'right',
      render: (row) => row.booking_call_id ? (
        <ToolbarButton variant="ghost" onClick={() => setOpen({ lead: row.id, call: row.booking_call_id })}>
          Read the call
        </ToolbarButton>
      ) : null,
    },
  ];

  const table = (
    <ListTable
      columns={columns}
      rows={rows}
      getRowKey={(row) => row.id}
      pageSize={25}
      loading={loading}
      defaultSort={{ key: 'when', direction: 'asc' }}
      onRowClick={(row) => setOpen({ lead: row.id })}
      emptyMessage={all.length
        ? 'Nothing matches. Clear the search or the filter.'
        : 'No callbacks booked yet. When a call ends with a time agreed, it lands here.'}
    />
  );
  // Search beside the title, the switch on the left of the toolbar, the
  // download on its right (the panel header's two rows, 21 Oct 2026).
  const searchBox = <SearchField value={query} onChange={setQuery} placeholder="Search callbacks…" />;
  const picker = (
    <SegmentedControl
      value={due}
      onChange={setDue}
      ariaLabel="When the callback is due"
      options={[
        { value: 'all', label: `All · ${all.length}` },
        { value: 'today', label: `Today · ${byDue('today').length}` },
        { value: 'tomorrow', label: `Tomorrow · ${byDue('tomorrow').length}` },
        { value: 'later', label: `Later · ${byDue('later').length}` },
      ]}
    />
  );
  const download = <ListDownload list="callbacks" total={all.length} noun="scheduled callbacks" />;

  return (
    <>
      <Panel
        title="Scheduled callbacks"
        subtitle={`${all.length} booked by the AI, each now with an advisor · ${reconnecting ? 'Reconnecting…' : 'soonest first'}`}
        search={searchBox}
        tools={picker}
        actions={download}
        expanded={{ content: table, actions: <>{searchBox}{picker}{download}</> }}
      >
        {error && !data ? <EmptyState message={error} /> : null}
        {error && !data ? null : table}
      </Panel>
      {open ? (
        <LeadStoryModal leadId={open.lead} callId={open.call} onClose={() => setOpen(null)} />
      ) : null}
    </>
  );
}
